"use client";

import { Check, Download } from "lucide-react";
import { FormDescriptionContent } from "@/features/forms/_components/renderer/FormDescriptionContent";
import { FORMS_EVENTS_MAILBOX } from "@/lib/forms/event-sender-address";

/** Filled-document generation status shown in the thank-you state. */
export type FilledFileStatus = "idle" | "pending" | "ready" | "failed";

/** Shared layout/typography for the download links; the `border` modifier + inline style pick filled vs outline. */
const DOWNLOAD_LINK_CLASS =
  "inline-flex flex-1 touch-manipulation items-center justify-center gap-2 whitespace-nowrap rounded-lg px-6 py-2.5 text-sm font-semibold transition-all [@media(hover:hover)]:hover:opacity-90";

interface FormThankYouProps {
  /** Form name, shown as an eyebrow above the "Thank you!" heading. */
  formName: string;
  /** Custom post-submission message configured on the form, if any. */
  confirmationMessage: string | null;
  /** Whether the form autofills a PDF/DOCX template. */
  hasFileTemplate: boolean;
  /** Whether the filled file is emailed to the respondent. */
  willEmailFile: boolean;
  /** Whether the form has a field a confirmation email could be sent to. */
  collectsEmail: boolean;
  /** Filled-document generation state + signed download URLs (once ready). */
  filledDoc: { status: FilledFileStatus; pdfUrl: string | null; docxUrl: string | null };
  /** Form theme color for the download buttons. */
  primaryColor: string;
  /** Readable text color over `primaryColor`. */
  buttonTextColor: string;
}

/**
 * Post-submission "Thank you" content for the public FormModal: the confirmation
 * message, an email reassurance for autofill forms, and the filled-document
 * download buttons / pending / failed states.
 *
 * @param props - Thank-you props
 * @returns Thank-you element
 */
export function FormThankYou({
  formName,
  confirmationMessage,
  hasFileTemplate,
  willEmailFile,
  collectsEmail,
  filledDoc,
  primaryColor,
  buttonTextColor,
}: FormThankYouProps) {
  const { status, pdfUrl, docxUrl } = filledDoc;

  // Derived values — filled (primary) vs outline (secondary) download-link styles.
  const filledStyle = {
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
    color: buttonTextColor,
  };
  const outlineStyle = { borderColor: primaryColor, color: primaryColor };

  return (
    <div className="flex flex-col items-center gap-6 pb-6 pt-1 text-center">
      {/* Success icon + heading + message all sit tight as one unit */}
      <div className="flex flex-col items-center gap-3">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full"
          style={{ backgroundColor: `${primaryColor}1A` }}
        >
          <Check className="h-7 w-7" strokeWidth={2.5} style={{ color: primaryColor }} aria-hidden="true" />
        </div>
        <div className="flex flex-col items-center gap-2">
          {formName && (
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: primaryColor }}>
              {formName}
            </span>
          )}
          <h3 className="text-2xl font-bold text-gray-900">Thank you!</h3>
          {/* Render through FormDescriptionContent (same as the standalone form confirmation page)
              so HTML messages — e.g. a "<a>Join the Slack channel</a>" link — are sanitized and
              rendered as real markup instead of literal text. Plain-text messages fall back to the
              same whitespace-preserving, auto-linked rendering. */}
          <FormDescriptionContent
            html={confirmationMessage || "Your response has been submitted successfully."}
            className="text-sm font-medium text-gray-600"
          />
          {/* No email field means no confirmation email goes out, so don't promise one. */}
          {collectsEmail && (
            <p className="text-sm font-medium text-gray-600">
              You&apos;ll receive a confirmation email from {FORMS_EVENTS_MAILBOX}. If you don&apos;t see it, check your
              spam folder.
            </p>
          )}
          {/* Autofill forms email the filled file, so reassure the respondent in
              every sub-state (still preparing, downloadable, or failed). */}
          {hasFileTemplate && willEmailFile && (
            <p className="text-sm font-medium text-gray-600">
              We&apos;ll email you a copy of your filled document shortly.
            </p>
          )}
        </div>
      </div>

      {status === "pending" && (
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <span
            className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600"
            aria-hidden="true"
          />
          Preparing your filled document…
        </div>
      )}

      {status === "ready" && (pdfUrl || docxUrl) && (
        // Show only the file(s) the form is configured to deliver (PDF only /
        // Word only / both). When both show, the PDF leads (filled) and Word
        // sits beside it (outline); when Word is the only download, it takes the
        // filled primary style. Stacks full-width on mobile.
        <div className="flex w-full max-w-sm flex-col gap-2 sm:flex-row">
          {pdfUrl && (
            <a href={pdfUrl} download className={DOWNLOAD_LINK_CLASS} style={filledStyle}>
              <Download className="h-4 w-4" aria-hidden="true" />
              Download PDF
            </a>
          )}
          {docxUrl && (
            <a
              href={docxUrl}
              download
              // Word is primary (filled) when it's the only download; outline when it sits beside the PDF.
              className={pdfUrl ? `${DOWNLOAD_LINK_CLASS} border` : DOWNLOAD_LINK_CLASS}
              style={pdfUrl ? outlineStyle : filledStyle}
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Download Word
            </a>
          )}
        </div>
      )}

      {/* If it won't be emailed and generation didn't finish in time, soften the
          wait (the email reassurance above already covers email forms). */}
      {status === "failed" && !willEmailFile && (
        <p className="text-sm font-medium text-gray-500">Your filled document will be ready shortly.</p>
      )}
    </div>
  );
}

export default FormThankYou;
